import React from 'react';
import { Slider } from '../../../components/Slider';
import { ColorPicker } from '../../../components/ColorPicker';
import { CollapsibleSection } from '../../../components/CollapsibleSection';
import { VFXEffectRegistry } from '../../../../effects/VFXEffectRegistry';
import { RAW, FONT_SIZE, SPACE } from '../../../theme';
import {
  VfxTabProps, SectionLabel, SelectBox, ActionButton,
  PrimitiveRow, PrimitiveHeader, TypeBadge, ButtonRow,
  EASINGS, PRIM_TYPES, DEFAULT_NEW_PRIMITIVE, eventBus,
  FIELD_LABEL, REMOVE_RED, COUNTER_GREY,
} from './shared';
import { livePlayerPosition } from '../../../../systems/player/livePlayerPosition';

export const VfxTab: React.FC<VfxTabProps> = ({ ability, update, player, getVfxId, getVfxConfig }) => {
  const allEffects = VFXEffectRegistry.getAllIds();
  const currentVfxId = getVfxId();
  const config = getVfxConfig();

  const playVfx = () => {
    if (!player) return;
    eventBus.emit('vfx:spawn', {
      effectId: currentVfxId,
      x: livePlayerPosition(player?.position).x + (player.facing === 'right' ? 40 : -40),
      y: livePlayerPosition(player?.position).y - 20,
      color: ability.vfx?.color,
      scale: ability.vfx?.scale,
      duration: ability.vfx?.duration,
    });
  };

  const patchPrim = (idx: number, patch: any) => {
    VFXEffectRegistry.updatePrimitive(currentVfxId, idx, patch);
  };

  return (
    <>
      <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Effect Preset</div>
      <SelectBox value={ability.vfx?.muzzleFlash || ''} onChange={e => update({ vfx: { muzzleFlash: e.target.value } })}>
        <option value="">None / Custom</option>
        {allEffects.map(id => <option key={id} value={id}>{VFXEffectRegistry.get(id)?.name || id}</option>)}
      </SelectBox>

      <CollapsibleSection title="Quick VFX" defaultOpen={false}>
        <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Impact Effect</div>
        <SelectBox value={ability.vfx?.impact || ''} onChange={e => {
          if (e.target.value) {
            const impactId = `${ability.id}_impact_${e.target.value}`;
            update({ vfx: { ...ability.vfx, impact: impactId } } as any);
          } else {
            update({ vfx: { ...ability.vfx, impact: undefined } } as any);
          }
        }}>
          <option value="">None</option>
          <option value="explosion">Explosion</option>
          <option value="spark">Spark</option>
          <option value="shatter">Shatter</option>
          <option value="freeze">Freeze</option>
          <option value="burn">Burn</option>
        </SelectBox>
        <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Cast Effect</div>
        <SelectBox value={ability.vfx?.cast || ''} onChange={e => {
          if (e.target.value) {
            const castId = `${ability.id}_cast_${e.target.value}`;
            update({ vfx: { ...ability.vfx, cast: castId } } as any);
          } else {
            update({ vfx: { ...ability.vfx, cast: undefined } } as any);
          }
        }}>
          <option value="">None</option>
          <option value="charge">Charge</option>
          <option value="glow">Glow</option>
          <option value="summon">Summon</option>
          <option value="channel">Channel</option>
        </SelectBox>
        <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Trail</div>
        <div style={{ display: 'flex', gap: SPACE.xs, alignItems: 'center', marginBottom: SPACE.md }}>
          <input type="checkbox" title="Enable trail" checked={!!ability.vfx?.trail} onChange={e => {
            update({ vfx: { ...ability.vfx, trail: e.target.checked ? `${ability.id}_trail` : undefined } } as any);
          }} />
          <span style={{ fontSize: FONT_SIZE.xs, color: RAW.gray }}>Enable Trail VFX</span>
        </div>
      </CollapsibleSection>

      <Slider label="Scale" value={ability.vfx?.scale ?? 1} min={0.1} max={5} step={0.1}
        onChange={v => update({ vfx: { scale: v } })} />
      <Slider label="Duration (ms)" value={ability.vfx?.duration ?? 300} min={50} max={2000} step={50}
        onChange={v => update({ vfx: { duration: v } })} />
      <ColorPicker label="Tint Override" value={ability.vfx?.color ?? 0xFFFFFF}
        onChange={v => update({ vfx: { color: v } })} />

      <ActionButton $variant="yellow" onClick={playVfx}>▶ Preview VFX</ActionButton>

      {config && (
        <>
          <SectionLabel>Primitives ({config.primitives.length})</SectionLabel>
          {config.primitives.map((prim: any, idx: number) => (
            <PrimitiveRow key={idx}>
              <PrimitiveHeader>
                <TypeBadge>{prim.type}</TypeBadge>
                <span style={{ fontSize: FONT_SIZE.xs, color: COUNTER_GREY }}>#{idx}</span>
                <span style={{ marginLeft: 'auto', cursor: 'pointer', color: REMOVE_RED, fontSize: FONT_SIZE.md }}
                  onClick={() => {
                    const prims = [...config!.primitives];
                    prims.splice(idx, 1);
                    VFXEffectRegistry.updateEffect(currentVfxId, { primitives: prims } as any);
                  }}>✕ Remove</span>
              </PrimitiveHeader>

              <ColorPicker label="Color" value={prim.color ?? 0xFFFFFF}
                onChange={v => patchPrim(idx, { color: v })} />
              <Slider label="Alpha" value={prim.alpha ?? 1} min={0} max={1} step={0.05}
                onChange={v => patchPrim(idx, { alpha: v })} />

              {prim.type === 'circle' && (
                <Slider label="Radius" value={prim.radius ?? 10} min={1} max={100} step={1}
                  onChange={v => patchPrim(idx, { radius: v })} />
              )}
              {(prim.type === 'rect' || prim.type === 'ellipse') && (
                <>
                  <Slider label="Width" value={prim.width ?? 20} min={1} max={200} step={1}
                    onChange={v => patchPrim(idx, { width: v })} />
                  <Slider label="Height" value={prim.height ?? 20} min={1} max={200} step={1}
                    onChange={v => patchPrim(idx, { height: v })} />
                </>
              )}

              <Slider label="Depth" value={prim.depth ?? 90} min={0} max={200} step={1}
                onChange={v => patchPrim(idx, { depth: v })} />

              {prim.stroke && (
                <CollapsibleSection title="Stroke">
                  <ColorPicker label="Stroke Color" value={prim.stroke.color ?? 0xFFFFFF}
                    onChange={v => patchPrim(idx, { stroke: { ...prim.stroke, color: v } })} />
                  <Slider label="Stroke Width" value={prim.stroke.width ?? 1} min={0} max={5} step={0.5}
                    onChange={v => patchPrim(idx, { stroke: { ...prim.stroke, width: v } })} />
                </CollapsibleSection>
              )}

              {prim.tween && (
                <CollapsibleSection title="Tween" badge={`${prim.tween.duration}ms`}>
                  <Slider label="Duration (ms)" value={prim.tween.duration} min={50} max={3000} step={50}
                    onChange={v => patchPrim(idx, { tween: { ...prim.tween, duration: v } })} />
                  <div style={{ fontSize: FONT_SIZE.xs, color: FIELD_LABEL, marginBottom: 3 }}>Easing</div>
                  <SelectBox value={prim.tween.ease || 'Power2'}
                    onChange={e => patchPrim(idx, { tween: { ...prim.tween, ease: e.target.value } })}>
                    {EASINGS.map(e => <option key={e} value={e}>{e}</option>)}
                  </SelectBox>
                  {prim.tween.alpha !== undefined && (
                    <Slider label="Alpha Target" value={prim.tween.alpha} min={0} max={1} step={0.05}
                      onChange={v => patchPrim(idx, { tween: { ...prim.tween, alpha: v } })} />
                  )}
                  {prim.tween.scale !== undefined && (
                    <Slider label="Scale Target" value={prim.tween.scale} min={0.1} max={5} step={0.1}
                      onChange={v => patchPrim(idx, { tween: { ...prim.tween, scale: v } })} />
                  )}
                  {prim.tween.rotation !== undefined && (
                    <Slider label="Rotation" value={prim.tween.rotation} min={0} max={Math.PI * 2} step={0.1}
                      formatValue={v => `${(v * 180 / Math.PI).toFixed(0)}°`}
                      onChange={v => patchPrim(idx, { tween: { ...prim.tween, rotation: v } })} />
                  )}
                  {prim.tween.yoyo !== undefined && (
                    <div style={{ fontSize: FONT_SIZE.sm, color: FIELD_LABEL, marginBottom: SPACE.sm }}>
                      Yoyo: <span style={{ color: prim.tween.yoyo ? '#6ee7b7' : RAW.gray }}>{prim.tween.yoyo ? 'ON' : 'OFF'}</span>
                    </div>
                  )}
                  {prim.tween.repeat !== undefined && (
                    <Slider label="Repeat" value={prim.tween.repeat} min={-1} max={20} step={1}
                      formatValue={v => v === -1 ? '∞' : String(v)}
                      onChange={v => patchPrim(idx, { tween: { ...prim.tween, repeat: v } })} />
                  )}
                </CollapsibleSection>
              )}

              {prim.scatter && (
                <CollapsibleSection title="Scatter" badge={`${prim.scatter.count}x`}>
                  <Slider label="Count" value={prim.scatter.count} min={1} max={20} step={1}
                    onChange={v => patchPrim(idx, { scatter: { ...prim.scatter, count: v } })} />
                  <Slider label="Radius" value={prim.scatter.radius} min={5} max={200} step={5}
                    onChange={v => patchPrim(idx, { scatter: { ...prim.scatter, radius: v } })} />
                </CollapsibleSection>
              )}

              {prim.orbit && (
                <CollapsibleSection title="Orbit" badge={`r:${prim.orbit.radius}`}>
                  <Slider label="Orbit Radius" value={prim.orbit.radius} min={10} max={200} step={5}
                    onChange={v => patchPrim(idx, { orbit: { ...prim.orbit, radius: v } })} />
                  <Slider label="Speed (rot/s)" value={prim.orbit.speed} min={0.1} max={5} step={0.1}
                    onChange={v => patchPrim(idx, { orbit: { ...prim.orbit, speed: v } })} />
                </CollapsibleSection>
              )}
            </PrimitiveRow>
          ))}

          {/* + Add Primitive */}
          <ButtonRow>
            <SelectBox id="add-prim-type" style={{ marginBottom: 0, flex: 1 }}>
              {PRIM_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
            </SelectBox>
            <ActionButton $variant="green" style={{ width: 'auto', padding: '5px 12px', marginBottom: 0 }}
              onClick={() => {
                const sel = (document.getElementById('add-prim-type') as HTMLSelectElement)?.value || 'circle';
                const newPrim: any = {
                  type: sel,
                  ...DEFAULT_NEW_PRIMITIVE,
                  ...(sel === 'circle' ? { radius: 10 } : { width: 20, height: 20 }),
                };
                const prims = [...config!.primitives, newPrim];
                VFXEffectRegistry.updateEffect(currentVfxId, { primitives: prims } as any);
              }}>+ Add</ActionButton>
          </ButtonRow>
        </>
      )}
    </>
  );
};
